import 'react-toastify/dist/ReactToastify.css'
import { ToastContainer } from 'react-toastify'
import { RouterProvider } from 'react-router-dom'
import { ThemeProvider, createTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from './app/hooks'
import { baseThemeOptions } from './theme/baseTheme'
import { useEffect } from 'react'
import {
  setUserInfo,
  setTheme,
  setCurrentUserType,
  logout,
} from './app/authSlice'
import { useGetUserInfoQuery } from './services/userApi'
import router from './routes'
import { GoogleOAuthProvider } from '@react-oauth/google'

const defaultTheme = baseThemeOptions

const PUBLIC_PATHS = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/logout',
  '/holder/register',
  '/register',
  '/pay', // Shows registration form when not logged in
  '/payment', // Backwards compatibility
  '/payment-blocked',
]

export default function App() {
  const dispatch = useAppDispatch()
  const {
    theme: selectedTheme,
    userToken,
    refreshToken,
  } = useAppSelector((state) => state.auth)

  useEffect(() => {
    if (!selectedTheme) {
      dispatch(setTheme('light'))
      localStorage.setItem('theme', 'light')
    }

    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)',
    ).matches

    if (
      selectedTheme === 'dark' ||
      (!('theme' in localStorage) && prefersDark)
    ) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [selectedTheme, dispatch])

  const isPublicRoute = PUBLIC_PATHS.some((path) =>
    location.pathname.startsWith(path),
  )

  const { data, isError } = useGetUserInfoQuery(0, {
    skip: !userToken,
    pollingInterval: userToken ? 60000 : undefined,
  })

  useEffect(() => {
    if (data) {
      dispatch(setUserInfo(data))

      if (!localStorage.getItem('userType')) {
        const perms = data.permissions || []
        const type =
          data.vendor === 'Stripe'
            ? perms.find(
                (p: string) =>
                  p === 'Holder' ||
                  (p !== 'Customer Admin' && p !== 'Customer Support'),
              ) || perms[0]
            : perms[0]
        if (type) {
          dispatch(setCurrentUserType(type))
          localStorage.setItem('userType', type)
        }
      }
    }

    if (isError && !isPublicRoute) {
      window.location.href = '/login'
    }
  }, [data, dispatch, isError, isPublicRoute])

  useEffect(() => {
    const currentIsPublicRoute = PUBLIC_PATHS.some((path) =>
      location.pathname.startsWith(path),
    )

    if (userToken && !refreshToken && !currentIsPublicRoute) {
      console.warn('Usuário sem refreshToken - forçando logout')
      dispatch(logout())
      localStorage.clear()
      window.location.href = '/logout'
    }
  }, [userToken, refreshToken, dispatch])

  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#5A9107',
      },
      text: {
        primary: '#121212',
      },
    },
    ...defaultTheme,
  })

  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#7dbf26',
        contrastText: '#fff',
      },
      text: {
        primary: '#FCFCFC',
      },
    },
    ...defaultTheme,
  })

  return (
    <GoogleOAuthProvider clientId="547406224436-usg2r39kmko40jk0qo5e60qng6m8dk1s.apps.googleusercontent.com">
      <ThemeProvider theme={selectedTheme === 'dark' ? darkTheme : theme}>
        <RouterProvider router={router} />
        <ToastContainer
          position="top-center"
          theme={selectedTheme === 'dark' ? 'dark' : 'light'}
        />
        {/* <NewsDialog /> */}
      </ThemeProvider>
    </GoogleOAuthProvider>
  )
}
